import {useCallback, useRef, useState} from 'react';
import {Rect, useRectEffect} from 'react-viewport-utils';

export const useExpanded = (
  elements: {
    wrapper: React.MutableRefObject<HTMLElement | null>;
    control: React.MutableRefObject<HTMLElement | null>;
    optionalControl: React.MutableRefObject<HTMLElement | null>;
    itemList: React.MutableRefObject<HTMLElement | null>;
  },
  isInitiallyExpanded = false,
) => {
  const {wrapper, control, itemList, optionalControl} = elements;

  const [expanded, setExpanded] = useState(isInitiallyExpanded);
  const toggleExpanded = useCallback(
    () => setExpanded(expanded => !expanded),
    [setExpanded],
  );

  const [itemsFitContainer, setItemsFitContainer] = useState(true);
  const shouldDisplayExpandedButton = !itemsFitContainer;

  // as the update depends on the rect that is returned by the observer, we have  to lazy evaluation to break the circular dependency. So the real update function is defined later.
  const update = useRef<() => void>(() => {
    throw new Error('Should never be called');
  });
  const updateStateLazy = useCallback(() => {
    update.current();
  }, [update]);

  const wrapperRect = useObserveRect(wrapper, updateStateLazy);
  const controlRect = useObserveRect(control, updateStateLazy);
  const optionalControlRect = useObserveRect(optionalControl, updateStateLazy);
  const itemListRect = useObserveRect(itemList, updateStateLazy);

  update.current = useCallback(() => {
    if (!wrapperRect.current || !itemListRect.current) {
      return;
    }
    const {width: wrapperWidth} = wrapperRect.current;
    const {width: itemListWidth} = itemListRect.current;
    const controlWidth = controlRect.current?.width ?? 0;
    // the optional control width is part of the control width, as it's a nested element. As we want do find out if this element should be displayed or not, we need to exclude it to get the best fit for the viewport
    const optionalControlWidth = optionalControlRect.current?.width ?? 0;
    const mandatoryControlWidth = controlWidth - optionalControlWidth;
    const visibleWrapperWidth = wrapperWidth - mandatoryControlWidth;

    setItemsFitContainer(visibleWrapperWidth > itemListWidth);
  }, [wrapperRect, itemListRect, controlRect, optionalControlRect]);

  return {expanded, shouldDisplayExpandedButton, toggleExpanded};
};

const useObserveRect = (
  ref: React.MutableRefObject<HTMLElement | null>,
  cb: () => void,
  dependencies?: ReadonlyArray<unknown>,
) => {
  const rectRef = useRef<Rect | null>(null);
  const update = useCallback(
    (rect: Rect | null) => {
      rectRef.current = rect;
      cb();
    },
    [cb],
  );
  useRectEffect(
    update,
    ref,
    {
      priority: 'low',
      deferUpdateUntilIdle: true,
    },
    dependencies,
  );

  return rectRef;
};
